import React, { useEffect, useRef, useState, useCallback } from 'react';
import { format, parse } from 'date-fns';
import {
  CalendarClock,
  Calendar,
  Clock,
  Layers,
  Loader2,
  Pencil,
  Copy,
  RefreshCw,
  User,
  UserMinus,
  UserPlus,
  Users,
  XCircle,
  Ban,
  Share2,
  Trash2,
  Info,
  CalendarDays,
  Package,
  Tent,
  Ticket,
  CheckCircle2,
  type LucideIcon,
} from 'lucide-react';
import { Drawer } from '@/components/ui/Drawer';
import { Button } from '@/components/ui/Button';
import { Tooltip, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Checkbox } from '@/components/ui';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { LessonShareModal } from '@/components/lessons/LessonShareModal';
import type { Coach, Lesson, LessonSession, Term } from '@court-sync/shared';
import {
  getLessonCoachIds,
  hasPerSessionStaffEnrollmentPrice,
  isScheduleOccurrencePast,
  shouldWarnWhenEditingLessonInRunningSchedule,
  shouldShowEnrollmentGuardian,
} from '@court-sync/shared';
import { UserAvatar } from '@/components/UserAvatar';
import { formatTimeHHMM } from '@/utils/timeFormat';
import type { LessonOccurrenceClickPayload } from '@/utils/scheduleOccurrences';
import {
  getLessonAgeGroupDisplayLabel,
  getLessonOccurrenceMetaLabelParts,
  getLessonProgramDisplayLabel,
  type LessonCalendarTooltipContext,
} from '@/utils/lessonCalendarTooltip';
import { getCampEnrollmentListScopeLabel, getEnrollmentListScopeLabel, getOccurrenceScopeLabel } from '@/utils/bookingScopeLabel';
import { lessonService, type LessonEnrollmentRow } from '@/services/lesson';
import { lessonSessionsService } from '@/services/lessonSessions';
import { lessonOverrideService } from '@/services/lessonOverride';
import { bookingService } from '@/services/booking';
import { invalidateBookingsQueries, patchBookingInQueries } from '@/queries/bookings';
import { coachService } from '@/services/coach';
import type { CoachSessionAttendanceRosterEntry } from '@court-sync/shared';
import { EnrollStudentModal } from '@/components/schedule/EnrollStudentModal';
import { AddCoachToSessionModal } from '@/components/lessons/AddCoachToSessionModal';
import toast from 'react-hot-toast';
import { LessonEditRunningScheduleWarningDialog } from '@/components/lessons/LessonEditRunningScheduleWarningDialog';
import { CancelTermLessonModal } from '@/components/lessons/CancelTermLessonModal';
import { CancelSessionModal } from '@/components/lessons/CancelSessionModal';
import { RemoveSessionModal } from '@/components/lessons/RemoveSessionModal';
import { RemoveStudentEnrollmentDialog } from '@/components/lessons/RemoveStudentEnrollmentDialog';
import {
  EnrollmentBookingStatusChip,
  type EnrollmentBookingStatus,
} from '@/components/lessons/EnrollmentBookingStatusChip';
import { AttendanceToggle } from '@/components/lessons/AttendanceToggle';
import { SportBadge } from '@/components/sport/SportBadge';
import { resolveAcademySport } from '@/lib/sportPresentation';
import {
  resolveLessonCalendarBaseHex,
  type AgeGroupCalendarColorLookup,
} from '@/utils/lessonSlotColors';
import type { AcademySport } from '@court-sync/shared';
import { LESSON_SESSION_TOUR_STEP_IDS } from '@/lib/tour-constants';
import { DrawerTourHelp } from '@/components/tour/DrawerTourHelp';
import { usePageTourActions } from '@/components/tour/PageTourHost';
import { scheduleAutoPageTour } from '@/lib/tours/scheduleAutoPageTour';
import { usePageTourReady } from '@/store/pageTourReady';
import { useAuthStore } from '@/store/auth';

const enrollmentScopeChipClass = 'inline-flex items-center gap-1 text-[10px] px-1.5 py-0 rounded';

const ENROLLMENT_SCOPE_CHIP: Record<string, { className: string; Icon: LucideIcon }> = {
  'Term booking': {
    className: `${enrollmentScopeChipClass} bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-100`,
    Icon: CalendarDays,
  },
  'Package booking': {
    className: `${enrollmentScopeChipClass} bg-indigo-100 text-indigo-900 dark:bg-indigo-900/40 dark:text-indigo-100`,
    Icon: Package,
  },
  'Camp day': {
    className: `${enrollmentScopeChipClass} bg-sky-100 text-sky-900 dark:bg-sky-900/50 dark:text-sky-100`,
    Icon: Tent,
  },
  'Drop-in': {
    className: `${enrollmentScopeChipClass} bg-sky-100 text-sky-900 dark:bg-sky-900/50 dark:text-sky-100`,
    Icon: Ticket,
  },
  'Single session': {
    className: `${enrollmentScopeChipClass} bg-sky-100 text-sky-900 dark:bg-sky-900/50 dark:text-sky-100`,
    Icon: CalendarClock,
  },
  Confirmed: {
    className: `${enrollmentScopeChipClass} bg-emerald-100 text-emerald-900 dark:bg-emerald-900/40 dark:text-emerald-100`,
    Icon: CheckCircle2,
  },
};

function EnrollmentScopeChip({ label }: { label: string }) {
  const config = ENROLLMENT_SCOPE_CHIP[label];
  if (!config) {
    return (
      <span
        className={`${enrollmentScopeChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}
      >
        {label}
      </span>
    );
  }
  const { className, Icon } = config;
  return (
    <span className={className}>
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      {label}
    </span>
  );
}

export interface LessonSessionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  occurrence: LessonOccurrenceClickPayload | null;
  coaches: Coach[];
  lessons: Lesson[];
  academyId: string;
  /** Academy slug for public booking URLs (standalone private copy link). */
  academySlug?: string;
  hasValidSubscription: boolean;
  /** Owner/admin: allow editing materialized session coach roster */
  allowSessionCoachOverride?: boolean;
  /** Resolve term-bound group lessons for “running schedule” edit warning */
  terms?: Term[];
  /** When false, session removal never offers make-up credit */
  makeupLessonEnabled?: boolean;
  onEdit: () => void;
  /** Duplicate standalone lesson into quick create (optional). */
  onDuplicate?: () => void;
  /** Parent opens postpone flow for this lesson + session date */
  onRequestPostpone: () => void;
  onAfterAction: () => void | Promise<void>;
  /** Optional lookups for age group / program labels in the drawer header */
  tooltipContext?: LessonCalendarTooltipContext;
  academySports?: readonly AcademySport[];
  ageGroupById?: Record<string, AgeGroupCalendarColorLookup>;
}

export function LessonSessionDetailModal({
  isOpen,
  onClose,
  occurrence,
  coaches,
  lessons: _lessons,
  academyId,
  academySlug: _academySlug,
  hasValidSubscription,
  allowSessionCoachOverride = true,
  terms,
  makeupLessonEnabled = false,
  onEdit,
  onDuplicate,
  onRequestPostpone,
  onAfterAction,
  tooltipContext,
  academySports = [],
  ageGroupById,
}: LessonSessionDetailModalProps) {
  const { user } = useAuthStore();
  const { startPageTour } = usePageTourActions();
  const sessionTourScheduledRef = useRef(false);
  const cancelSessionTourScheduleRef = useRef<(() => void) | null>(null);
  const [drawerPanelOpen, setDrawerPanelOpen] = useState(false);

  const [enrollments, setEnrollments] = useState<LessonEnrollmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [removingBookingId, setRemovingBookingId] = useState<string | null>(null);
  const [confirmingBookingId, setConfirmingBookingId] = useState<string | null>(null);
  const [markingBookingId, setMarkingBookingId] = useState<string | null>(null);
  const [removeConfirm, setRemoveConfirm] = useState<{
    bookingId: string;
    studentName: string;
    isTermEnrollment: boolean;
  } | null>(null);
  const [enrollModalInstance, setEnrollModalInstance] = useState(0);
  const [showAddCoachModal, setShowAddCoachModal] = useState(false);
  const [addCoachModalInstance, setAddCoachModalInstance] = useState(0);
  const [removeCoachConfirm, setRemoveCoachConfirm] = useState<{
    coachId: string;
    coachName: string;
  } | null>(null);
  const [removeCoachNotifyEmail, setRemoveCoachNotifyEmail] = useState(true);
  const [removingSessionCoachId, setRemovingSessionCoachId] = useState<string | null>(null);
  const [editRunningScheduleWarningOpen, setEditRunningScheduleWarningOpen] = useState(false);
  const [resolvedMaterializedSession, setResolvedMaterializedSession] = useState<LessonSession | null>(null);
  const [resolvingMaterializedSession, setResolvingMaterializedSession] = useState(false);
  const [cancelTermModalOpen, setCancelTermModalOpen] = useState(false);
  const [cancelSessionModalOpen, setCancelSessionModalOpen] = useState(false);
  const [removeSessionModalOpen, setRemoveSessionModalOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [coachAttendanceRoster, setCoachAttendanceRoster] = useState<CoachSessionAttendanceRosterEntry[]>([]);
  const [loadingCoachAttendance, setLoadingCoachAttendance] = useState(false);
  const [markingCoachId, setMarkingCoachId] = useState<string | null>(null);
  const [localLessonCoachIds, setLocalLessonCoachIds] = useState<string[] | null>(null);

  const loadEnrollments = useCallback(async () => {
    if (!occurrence || !academyId) return;
    setLoading(true);
    try {
      const res = await lessonService.getLessonEnrollments(occurrence.lesson.id, {
        academyId,
        sessionDate: occurrence.sessionDateYmd,
      });
      if (res.success && res.data) setEnrollments(res.data);
      else setEnrollments([]);
    } catch {
      setEnrollments([]);
      toast.error('Failed to load students for this session');
    } finally {
      setLoading(false);
    }
  }, [occurrence, academyId]);

  const loadCoachAttendance = useCallback(async () => {
    if (!occurrence || !academyId) return;
    setLoadingCoachAttendance(true);
    try {
      const sessionId = occurrence.sessionId ?? resolvedMaterializedSession?.id;
      const roster = await coachService.getSessionCoachAttendance({
        academyId,
        lessonId: occurrence.lesson.id,
        sessionDate: occurrence.sessionDateYmd,
        ...(sessionId ? { sessionId } : {}),
      });
      setCoachAttendanceRoster(roster);
    } catch {
      setCoachAttendanceRoster([]);
    } finally {
      setLoadingCoachAttendance(false);
    }
  }, [occurrence, academyId, resolvedMaterializedSession?.id]);

  const loadMaterializedSession = useCallback(async () => {
    if (!occurrence || !academyId) return;
    if (!allowSessionCoachOverride || occurrence.sessionId) {
      setResolvedMaterializedSession(null);
      return;
    }
    if (occurrence.status !== 'scheduled') {
      setResolvedMaterializedSession(null);
      return;
    }
    setResolvingMaterializedSession(true);
    try {
      const rows = await lessonSessionsService.listByAcademyAndDateRange(
        academyId,
        occurrence.sessionDateYmd,
        occurrence.sessionDateYmd
      );
      const hit = rows.find((s) => {
        if (s.lessonId !== occurrence.lesson.id) return false;
        const ymd = s.sessionDate.replace(/T.*/, '').slice(0, 10);
        return ymd === occurrence.sessionDateYmd;
      });
      setResolvedMaterializedSession(hit ?? null);
    } catch {
      setResolvedMaterializedSession(null);
    } finally {
      setResolvingMaterializedSession(false);
    }
  }, [occurrence, academyId, allowSessionCoachOverride]);

  const refreshLessonCoachIds = useCallback(async () => {
    if (!occurrence) return;
    try {
      const res = await lessonService.getLesson(occurrence.lesson.id);
      if (res.success && res.data) {
        setLocalLessonCoachIds(getLessonCoachIds(res.data));
      }
    } catch {
      setLocalLessonCoachIds(null);
    }
  }, [occurrence]);

  const refreshCoachRoster = useCallback(async () => {
    await Promise.all([loadMaterializedSession(), refreshLessonCoachIds()]);
  }, [loadMaterializedSession, refreshLessonCoachIds]);

  /** Keep session coach roster in sync with API (calendar click payload can be stale). */
  useEffect(() => {
    if (!isOpen || !occurrence || !academyId) {
      setResolvedMaterializedSession(null);
      setLocalLessonCoachIds(null);
      setResolvingMaterializedSession(false);
      return () => {};
    }
    if (allowSessionCoachOverride && !occurrence.sessionId) {
      void loadMaterializedSession();
    } else {
      setResolvedMaterializedSession(null);
      setResolvingMaterializedSession(false);
    }
    void refreshLessonCoachIds();
    return () => {};
  }, [
    isOpen,
    occurrence,
    academyId,
    allowSessionCoachOverride,
    loadMaterializedSession,
    refreshLessonCoachIds,
  ]);

  useEffect(() => {
    if (isOpen && occurrence) {
      setCancelSessionModalOpen(false);
      setRemoveSessionModalOpen(false);
      setCancelTermModalOpen(false);
      setShowEnrollModal(false);
      setRemoveConfirm(null);
      setEditRunningScheduleWarningOpen(false);
      loadEnrollments();
    }
  }, [isOpen, occurrence, loadEnrollments]);

  useEffect(() => {
    if (isOpen && occurrence && academyId) {
      void loadCoachAttendance();
    }
  }, [isOpen, occurrence, academyId, resolvedMaterializedSession?.id, loadCoachAttendance]);

  useEffect(() => {
    if (!isOpen) {
      setCancelTermModalOpen(false);
      setCancelSessionModalOpen(false);
      setRemoveSessionModalOpen(false);
      setShareModalOpen(false);
    }
  }, [isOpen]);

  const openEnrollModal = useCallback(() => {
    setEnrollModalInstance((k) => k + 1);
    setShowEnrollModal(true);
  }, []);

  const openAddCoachModal = useCallback(() => {
    setAddCoachModalInstance((k) => k + 1);
    setShowAddCoachModal(true);
  }, []);

  const handleRemoveFromLesson = useCallback(
    async (
      bookingId: string,
      params: { scope: 'session' | 'term'; grantMakeupCredit: boolean }
    ): Promise<boolean> => {
      setRemovingBookingId(bookingId);
      try {
        const res = await bookingService.removeEnrollment(bookingId, {
          academyId,
          scope: params.scope,
          sessionDate: params.scope === 'session' ? occurrence?.sessionDateYmd : undefined,
          grantMakeupCredit: params.grantMakeupCredit,
        });
        if (res.success) {
          toast.success(
            params.scope === 'term'
              ? 'Student removed from term lesson'
              : res.data?.makeupCreditGranted
                ? 'Student removed and make-up credit granted'
                : 'Student removed from this session'
          );
          if (
            params.scope === 'session' &&
            params.grantMakeupCredit &&
            !res.data?.makeupCreditGranted &&
            res.data?.makeupCreditSkippedReason
          ) {
            toast.error(res.data.makeupCreditSkippedReason);
          }
          await loadEnrollments();
          await onAfterAction();
          return true;
        }
        toast.error((res as { error?: string }).error || 'Failed to remove student');
        return false;
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Failed to remove student');
        return false;
      } finally {
        setRemovingBookingId(null);
      }
    },
    [academyId, occurrence?.sessionDateYmd, loadEnrollments, onAfterAction]
  );

  const handleBookingStatusChange = useCallback(
    async (bookingId: string, status: EnrollmentBookingStatus) => {
      setConfirmingBookingId(bookingId);
      try {
        const res = await bookingService.updateBooking(bookingId, { status });
        if (res.success) {
          patchBookingInQueries(bookingId, { status });
          invalidateBookingsQueries();
          toast.success(
            status === 'confirmed' ? 'Booking confirmed' : 'Booking marked unconfirmed'
          );
          await loadEnrollments();
          await onAfterAction();
        } else {
          toast.error((res as { error?: string }).error || 'Failed to update booking status');
        }
      } catch (e: unknown) {
        toast.error(e instanceof Error ? e.message : 'Failed to update booking status');
      } finally {
        setConfirmingBookingId(null);
      }
    },
    [loadEnrollments, onAfterAction]
  );

  const handleMarkAttendance = useCallback(
    async (bookingId: string, status: 'scheduled' | 'attended' | 'missed' | 'cancelled') => {
      if (!occurrence) return;
      const previousStatus =
        enrollments.find((r) => r.bookingId === bookingId)?.attendanceStatus ?? 'scheduled';
      const sessionId = occurrence.sessionId ?? resolvedMaterializedSession?.id;

      setMarkingBookingId(bookingId);
      setEnrollments((prev) =>
        prev.map((r) => (r.bookingId === bookingId ? { ...r, attendanceStatus: status } : r))
      );

      try {
        const res = await lessonService.markSessionAttendance(occurrence.lesson.id, {
          academyId,
          sessionDate: occurrence.sessionDateYmd,
          ...(sessionId ? { sessionId } : {}),
          records: [{ bookingId, status }],
        });
        if (res.success) {
          toast.success(status === 'scheduled' ? 'Attendance cleared' : 'Attendance saved');
          await onAfterAction();
        } else {
          setEnrollments((prev) =>
            prev.map((r) =>
              r.bookingId === bookingId ? { ...r, attendanceStatus: previousStatus } : r
            )
          );
          toast.error((res as { error?: string }).error || 'Failed to save attendance');
          await loadEnrollments();
        }
      } catch (e: unknown) {
        setEnrollments((prev) =>
          prev.map((r) =>
            r.bookingId === bookingId ? { ...r, attendanceStatus: previousStatus } : r
          )
        );
        toast.error(e instanceof Error ? e.message : 'Failed to save attendance');
        await loadEnrollments();
      } finally {
        setMarkingBookingId(null);
      }
    },
    [
      occurrence,
      academyId,
      resolvedMaterializedSession?.id,
      enrollments,
      onAfterAction,
      loadEnrollments,
    ]
  );

  const handleMarkCoachAttendance = useCallback(
    async (coachId: string, status: 'scheduled' | 'attended' | 'missed' | 'cancelled') => {
      if (!occurrence) return;
      const previous =
        coachAttendanceRoster.find((r) => r.coachId === coachId)?.status ?? 'scheduled';
      const sessionId = occurrence.sessionId ?? resolvedMaterializedSession?.id;

      setMarkingCoachId(coachId);
      setCoachAttendanceRoster((prev) =>
        prev.map((r) => (r.coachId === coachId ? { ...r, status } : r))
      );

      try {
        await coachService.markSessionCoachAttendance({
          academyId,
          lessonId: occurrence.lesson.id,
          sessionDate: occurrence.sessionDateYmd,
          ...(sessionId ? { sessionId } : {}),
          records: [{ coachId, status }],
        });
        toast.success(status === 'scheduled' ? 'Coach attendance cleared' : 'Coach attendance saved');
      } catch (e: unknown) {
        setCoachAttendanceRoster((prev) =>
          prev.map((r) => (r.coachId === coachId ? { ...r, status: previous } : r))
        );
        toast.error(e instanceof Error ? e.message : 'Failed to save coach attendance');
        await loadCoachAttendance();
      } finally {
        setMarkingCoachId(null);
      }
    },
    [occurrence, academyId, resolvedMaterializedSession?.id, coachAttendanceRoster, loadCoachAttendance]
  );

  useEffect(() => {
    if (!isOpen) {
      setDrawerPanelOpen(false);
      sessionTourScheduledRef.current = false;
      cancelSessionTourScheduleRef.current?.();
      cancelSessionTourScheduleRef.current = null;
    }
  }, [isOpen]);

  const handleSessionDrawerPanelOpen = useCallback(() => {
    setDrawerPanelOpen(true);
    if (!isOpen || !occurrence || !hasValidSubscription || sessionTourScheduledRef.current) {
      return;
    }
    sessionTourScheduledRef.current = true;
    cancelSessionTourScheduleRef.current?.();
    cancelSessionTourScheduleRef.current = scheduleAutoPageTour(
      startPageTour,
      'academy-lesson-session',
      user?.preferences,
    );
  }, [hasValidSubscription, isOpen, occurrence, startPageTour, user?.preferences]);

  usePageTourReady(
    'academy-lesson-session',
    drawerPanelOpen && !!occurrence && hasValidSubscription
  );

  if (!occurrence) return null;

  const lesson = occurrence.lesson;
  const termForLesson =
    lesson.termId && terms?.length ? terms.find((t) => t.id === lesson.termId) : undefined;
  const shouldWarnRunningScheduleEdit = shouldWarnWhenEditingLessonInRunningSchedule(
    lesson,
    termForLesson ?? null,
    new Date()
  );
  const effectiveSessionId = occurrence.sessionId ?? resolvedMaterializedSession?.id;
  const resolvedCoachIds =
    resolvedMaterializedSession?.coachIds && resolvedMaterializedSession.coachIds.length > 0
      ? resolvedMaterializedSession.coachIds
      : localLessonCoachIds && localLessonCoachIds.length > 0
        ? localLessonCoachIds
        : occurrence.sessionCoachIds && occurrence.sessionCoachIds.length > 0
          ? occurrence.sessionCoachIds
          : getLessonCoachIds(lesson);
  const displayHasCoachOverride =
    resolvedMaterializedSession?.hasCoachOverride ?? occurrence.hasCoachOverride;
  const displayCoachOverrideCreatedAt =
    resolvedMaterializedSession?.coachOverrideCreatedAt ?? occurrence.coachOverrideCreatedAt;
  const assignedCoaches = resolvedCoachIds
    .map((id) => coaches.find((c) => c.id === id))
    .filter((c): c is Coach => !!c);

  const sessionScheduled = occurrence.status === 'scheduled';
  const sessionCancellable =
    occurrence.status === 'scheduled' || occurrence.status === 'postponed';
  /** Term group lessons need termId; camps use full-camp enroll API (no term). */
  const isTermGroupRoster = lesson.lessonType === 'group' && !!lesson.termId;
  const isCampRoster = lesson.lessonType === 'camp';
  const isPrivateRoster = lesson.lessonType === 'private';
  const canManageRoster =
    hasValidSubscription &&
    sessionScheduled &&
    !lesson.cancelledFromDate &&
    (isTermGroupRoster || isCampRoster || isPrivateRoster);
  const canAddStudent = canManageRoster;
  const canAddCoach = canManageRoster;
  const sessionIsPast = isScheduleOccurrencePast(
    parse(occurrence.sessionDateYmd, 'yyyy-MM-dd', new Date()),
    occurrence.endTime
  );
  const canShareLesson = true;
  const canTermActions = !!lesson.termId && !lesson.cancelledFromDate;
  const canPostpone = canTermActions && sessionScheduled && lesson.lessonType === 'group';
  const canEditSessionCoaches =
    allowSessionCoachOverride && sessionScheduled && !!effectiveSessionId;
  const showCoachRemoveCol = canEditSessionCoaches;
  const showCoachAttendanceCol = canManageRoster && assignedCoaches.length > 0;
  const showCoachListGrid = showCoachAttendanceCol || showCoachRemoveCol;
  const coachListGridCols =
    showCoachAttendanceCol && showCoachRemoveCol
      ? 'grid-cols-[minmax(0,1fr)_6.5rem_2rem]'
      : showCoachAttendanceCol
        ? 'grid-cols-[minmax(0,1fr)_6.5rem]'
        : showCoachRemoveCol
          ? 'grid-cols-[minmax(0,1fr)_2rem]'
          : '';
  const coachListRowGridCols =
    showCoachAttendanceCol && showCoachRemoveCol
      ? 'sm:grid-cols-[minmax(0,1fr)_6.5rem_2rem]'
      : showCoachAttendanceCol
        ? 'sm:grid-cols-[minmax(0,1fr)_6.5rem]'
        : showCoachRemoveCol
          ? 'sm:grid-cols-[minmax(0,1fr)_2rem]'
          : '';
  const showMissingMaterializedSessionHint =
    allowSessionCoachOverride &&
    sessionScheduled &&
    !effectiveSessionId &&
    !resolvingMaterializedSession;
  const isStandalonePrivate = lesson.lessonType === 'private' && !lesson.termId;
  const isTermBoundPrivate = lesson.lessonType === 'private' && !!lesson.termId;
  const packageOrTermCancelled = !!lesson.cancelledFromDate;
  const hasConfirmedEnrollments = enrollments.some((row) => row.status === 'confirmed');
  const hasMaterializedSession =
    !!effectiveSessionId || occurrence.status === 'cancelled' || occurrence.status === 'postponed';
  /** Remove empty sessions (no confirmed roster); cancel when families are enrolled. */
  const canRemoveSession =
    hasValidSubscription &&
    hasMaterializedSession &&
    !hasConfirmedEnrollments &&
    (sessionScheduled || occurrence.status === 'cancelled' || occurrence.status === 'postponed');
  const canCancelSession = sessionScheduled && hasConfirmedEnrollments;
  const canCancelPackage =
    sessionCancellable && isStandalonePrivate && !packageOrTermCancelled;
  const canCancelTerm =
    sessionCancellable &&
    !packageOrTermCancelled &&
    ((lesson.lessonType === 'group' && !!lesson.termId) || isTermBoundPrivate);

  const hasSessionMoreActions =
    canPostpone ||
    canRemoveSession ||
    canCancelSession ||
    canCancelPackage ||
    canCancelTerm ||
    canShareLesson ||
    !!onDuplicate;

  const sessionEnrollmentCount = enrollments.filter(
    (row) => row.status === 'pending' || row.status === 'confirmed'
  ).length;

  const cancelUnavailableReason = (() => {
    if (occurrence.status === 'cancelled') {
      if (hasConfirmedEnrollments) {
        return 'This session is cancelled. Confirmed students remain on the roster below.';
      }
      const recoveryHint = (() => {
        if (lesson.lessonType === 'group') {
          return 'To run this lesson on a new date, use Postpone session on a scheduled occurrence, or edit the lesson schedule.';
        }
        if (isStandalonePrivate) {
          return 'To offer this date again, edit the private lesson package and add or restore the session date.';
        }
        if (isTermBoundPrivate) {
          return 'To offer this date again, edit the lesson schedule in Term Lessons, or remove this cancelled slot and enroll students on a scheduled session.';
        }
        if (lesson.lessonType === 'camp') {
          return 'Edit the camp schedule to restore this day, or remove this cancelled slot from the calendar.';
        }
        return null;
      })();
      if (recoveryHint) {
        return `Add student is unavailable because this session is cancelled. ${recoveryHint}`;
      }
      return 'This session is cancelled but has no confirmed enrollments. Use Remove session to clear it from the schedule.';
    }
    if (packageOrTermCancelled && !canCancelSession) {
      return isStandalonePrivate
        ? 'This private lesson package was cancelled.'
        : 'This lesson was cancelled for the term.';
    }
    if (packageOrTermCancelled && canCancelSession) {
      return isStandalonePrivate
        ? 'The package was cancelled, but this session is still open on the schedule. You can cancel this session below to close it.'
        : 'The term lesson was cancelled, but this session is still open. You can cancel this session below.';
    }
    return null;
  })();

  const dateLabel = format(occurrence.date, 'EEEE, MMM d, yyyy');
  const sessionStatusLabel =
    occurrence.status === 'scheduled'
      ? 'Scheduled'
      : occurrence.status === 'cancelled'
        ? 'Cancelled'
        : 'Postponed';
  const sessionStatusClass =
    occurrence.status === 'scheduled'
      ? 'bg-green-100 dark:bg-green-900/40 text-green-900 dark:text-green-100'
      : occurrence.status === 'cancelled'
        ? 'bg-red-100 dark:bg-red-900/40 text-red-900 dark:text-red-100'
        : 'bg-amber-100 dark:bg-amber-900/40 text-amber-900 dark:text-amber-100';
  const ageGroupLabel = getLessonAgeGroupDisplayLabel(lesson, tooltipContext?.ageGroupLabelById);
  const ageGroupCalendarColor =
    lesson.lessonType === 'group'
      ? resolveLessonCalendarBaseHex(lesson, ageGroupById)
      : undefined;
  const sportPresentation = resolveAcademySport(academySports, lesson.academySportId);
  const programLabel = getLessonProgramDisplayLabel(lesson, tooltipContext?.programNameById);
  const descriptionChipClass = 'inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded';
  const drawerDescription = (
    <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span
        className={`${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}
      >
        <Calendar className="h-3 w-3 shrink-0" aria-hidden />
        {dateLabel}
      </span>
      <span
        className={`${descriptionChipClass} bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300`}
      >
        <Clock className="h-3 w-3 shrink-0" aria-hidden />
        {formatTimeHHMM(occurrence.startTime)} – {formatTimeHHMM(occurrence.endTime)}
      </span>
      {ageGroupLabel && (
        <span
          className={`${descriptionChipClass} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`}
        >
          {ageGroupCalendarColor ? (
            <span
              className="inline-block h-2.5 w-2.5 shrink-0 rounded-full border border-black/10"
              style={{ backgroundColor: ageGroupCalendarColor }}
              title="Age group calendar colour"
              aria-hidden
            />
          ) : (
            <Users className="h-3 w-3 shrink-0" aria-hidden />
          )}
          {ageGroupLabel}
        </span>
      )}
      {sportPresentation ? <SportBadge sport={sportPresentation} size="sm" /> : null}
      {programLabel && (
        <span
          className={`${descriptionChipClass} bg-violet-100 text-violet-900 dark:bg-violet-900/40 dark:text-violet-100`}
        >
          <Layers className="h-3 w-3 shrink-0" aria-hidden />
          {programLabel}
        </span>
      )}
      <span className={`${descriptionChipClass} ${sessionStatusClass}`}>{sessionStatusLabel}</span>
      {lesson.lessonType === 'private' && (
        <span
          className={`${descriptionChipClass} bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-100`}
        >
          <User className="h-3 w-3 shrink-0" aria-hidden />
          Private
        </span>
      )}
      {occurrence.makeupBookingCount != null && occurrence.makeupBookingCount > 0 && (
        <span
          className={`${descriptionChipClass} bg-purple-100 text-purple-900 dark:bg-purple-900/40 dark:text-purple-100`}
        >
          <RefreshCw className="h-3 w-3 shrink-0" aria-hidden />
          Make-up ({occurrence.makeupBookingCount})
        </span>
      )}
    </span>
  );
  const lessonTrackLabel =
    getLessonOccurrenceMetaLabelParts(lesson, tooltipContext).join(' · ') || null;

  const handleCancelSessionConfirm = async (
    reason: string,
    notifyStudents: boolean,
    grantMakeupCredit: boolean
  ) => {
    await lessonOverrideService.cancelLesson(
      lesson.id,
      occurrence.sessionDateYmd,
      reason.trim(),
      notifyStudents,
      academyId,
      grantMakeupCredit
    );
    toast.success(
      notifyStudents
        ? 'Session cancelled. Students have been notified where applicable.'
        : 'Session cancelled.'
    );
    setCancelSessionModalOpen(false);
    onClose();
    await onAfterAction();
  };

  const handleRemoveSessionConfirm = async () => {
    await lessonOverrideService.removeSession(
      lesson.id,
      occurrence.sessionDateYmd,
      academyId
    );
    toast.success('Session removed from the schedule');
    setRemoveSessionModalOpen(false);
    onClose();
    await onAfterAction();
  };

  const handleCancelTermConfirm = async (
    reason: string,
    notifyStudents: boolean,
    grantMakeupCredit: boolean
  ) => {
    await lessonOverrideService.cancelTermLesson(
      lesson.id,
      academyId,
      reason.trim(),
      occurrence.sessionDateYmd,
      notifyStudents,
      grantMakeupCredit
    );
    toast.success(
      lesson.lessonType === 'private' && !lesson.termId
        ? 'Package cancelled.'
        : 'Term lesson cancelled.'
    );
    setCancelTermModalOpen(false);
    onClose();
    await onAfterAction();
  };

  const handleConfirmRemoveCoachFromSession = async () => {
    if (!removeCoachConfirm || !effectiveSessionId) return;
    const { coachId } = removeCoachConfirm;
    const nextCoachIds = resolvedCoachIds.filter((id) => id !== coachId);
    if (nextCoachIds.length === 0 && !displayHasCoachOverride) {
      toast.error(
        'Cannot remove the only coach for this session. Edit the lesson default roster instead.'
      );
      setRemoveCoachConfirm(null);
      return;
    }

    setRemovingSessionCoachId(coachId);
    try {
      await lessonOverrideService.replaceSessionCoaches({
        academyId,
        sessionId: effectiveSessionId,
        coachIds: nextCoachIds,
        notifyCoachEmail: removeCoachNotifyEmail,
      });
      toast.success(
        nextCoachIds.length === 0
          ? 'Coach removed. This session uses the lesson default roster again.'
          : 'Coach removed from this session'
      );
      setRemoveCoachConfirm(null);
      await refreshCoachRoster();
      await loadCoachAttendance();
      await onAfterAction();
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : 'Failed to remove coach');
    } finally {
      setRemovingSessionCoachId(null);
    }
  };

  return (
    <>
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={occurrence.lessonName}
      width="3xl"
      description={drawerDescription}
      panelId={LESSON_SESSION_TOUR_STEP_IDS.DRAWER}
      descriptionId={LESSON_SESSION_TOUR_STEP_IDS.OVERVIEW}
      onPanelOpen={handleSessionDrawerPanelOpen}
      headerActions={
        hasValidSubscription ? (
          <DrawerTourHelp
            tourKey="academy-lesson-session"
            suggestion="First time managing a session? Tour coaches, students, and session actions."
            buttonLabel="Tour this session"
          />
        ) : undefined
      }
      footer={
        <div
          id={LESSON_SESSION_TOUR_STEP_IDS.ACTIONS}
          className="flex flex-wrap items-center gap-2"
        >
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => {
              if (shouldWarnRunningScheduleEdit) {
                setEditRunningScheduleWarningOpen(true);
              } else {
                onEdit();
                onClose();
              }
            }}
            disabled={!hasValidSubscription}
            className="min-w-0 flex-1 gap-1.5 sm:flex-initial"
          >
            <Pencil className="h-4 w-4 shrink-0" />
            Edit lesson
          </Button>
          {hasSessionMoreActions && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!hasValidSubscription}
                  className="shrink-0 sm:hidden"
                >
                  More…
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={4} className="w-56">
                {canPostpone && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onRequestPostpone();
                      onClose();
                    }}
                    className="cursor-pointer gap-2 text-amber-700 focus:text-amber-700 dark:text-amber-300 dark:focus:text-amber-300"
                  >
                    <CalendarClock className="h-4 w-4 shrink-0" />
                    Postpone session
                  </DropdownMenuItem>
                )}
                {canShareLesson && (
                  <DropdownMenuItem
                    onSelect={() => setShareModalOpen(true)}
                    className="cursor-pointer gap-2"
                  >
                    <Share2 className="h-4 w-4 shrink-0" />
                    Share lesson
                  </DropdownMenuItem>
                )}
                {onDuplicate && (
                  <DropdownMenuItem
                    onSelect={() => {
                      onDuplicate();
                      onClose();
                    }}
                    className="cursor-pointer gap-2"
                  >
                    <Copy className="h-4 w-4 shrink-0" />
                    Duplicate lesson
                  </DropdownMenuItem>
                )}
                {(canPostpone || canShareLesson || onDuplicate) &&
                  (canRemoveSession || canCancelSession || canCancelPackage || canCancelTerm) && (
                  <DropdownMenuSeparator />
                )}
                {canRemoveSession && (
                  <DropdownMenuItem
                    onSelect={() => setRemoveSessionModalOpen(true)}
                    className="cursor-pointer gap-2 text-red-700 focus:text-red-700 dark:text-red-300 dark:focus:text-red-300"
                  >
                    <Trash2 className="h-4 w-4 shrink-0" />
                    Remove session
                  </DropdownMenuItem>
                )}
                {canCancelSession && (
                  <DropdownMenuItem
                    onSelect={() => setCancelSessionModalOpen(true)}
                    className="cursor-pointer gap-2 text-red-700 focus:text-red-700 dark:text-red-300 dark:focus:text-red-300"
                  >
                    <XCircle className="h-4 w-4 shrink-0" />
                    Cancel session
                  </DropdownMenuItem>
                )}
                {canCancelPackage && (
                  <DropdownMenuItem
                    onSelect={() => setCancelTermModalOpen(true)}
                    className="cursor-pointer gap-2 text-red-800 focus:text-red-800 dark:text-red-200 dark:focus:text-red-200"
                  >
                    <Ban className="h-4 w-4 shrink-0" />
                    Cancel package
                  </DropdownMenuItem>
                )}
                {canCancelTerm && (
                  <DropdownMenuItem
                    onSelect={() => setCancelTermModalOpen(true)}
                    className="cursor-pointer gap-2 text-red-800 focus:text-red-800 dark:text-red-200 dark:focus:text-red-200"
                  >
                    <Ban className="h-4 w-4 shrink-0" />
                    {isTermBoundPrivate ? 'Cancel term lessons' : 'Cancel term lesson'}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {canPostpone && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onRequestPostpone();
                onClose();
              }}
              disabled={!hasValidSubscription}
              className="hidden gap-1.5 text-amber-700 dark:text-amber-300 sm:inline-flex"
            >
              <CalendarClock className="h-4 w-4" />
              Postpone session
            </Button>
          )}
          {canRemoveSession && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRemoveSessionModalOpen(true)}
              disabled={!hasValidSubscription}
              className="hidden gap-1.5 text-red-700 dark:text-red-300 sm:inline-flex"
            >
              <Trash2 className="h-4 w-4" />
              Remove session
            </Button>
          )}
          {canCancelSession && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCancelSessionModalOpen(true)}
              disabled={!hasValidSubscription}
              className="hidden gap-1.5 text-red-700 dark:text-red-300 sm:inline-flex"
            >
              <XCircle className="h-4 w-4" />
              Cancel session
            </Button>
          )}
          {canCancelPackage && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCancelTermModalOpen(true)}
              disabled={!hasValidSubscription}
              className="hidden gap-1.5 text-red-800 dark:text-red-200 sm:inline-flex"
            >
              <Ban className="h-4 w-4" />
              Cancel package
            </Button>
          )}
          {canCancelTerm && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setCancelTermModalOpen(true)}
              disabled={!hasValidSubscription}
              className="hidden gap-1.5 text-red-800 dark:text-red-200 sm:inline-flex"
            >
              <Ban className="h-4 w-4" />
              {isTermBoundPrivate ? 'Cancel term lessons' : 'Cancel term lesson'}
            </Button>
          )}
          {canShareLesson && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setShareModalOpen(true)}
              disabled={!hasValidSubscription}
              className="hidden gap-1.5 sm:inline-flex"
            >
              <Share2 className="h-4 w-4" />
              Share lesson
            </Button>
          )}
          {onDuplicate && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                onDuplicate();
                onClose();
              }}
              disabled={!hasValidSubscription}
              className="hidden gap-1.5 sm:inline-flex"
            >
              <Copy className="h-4 w-4" />
              Duplicate
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col min-h-full gap-4 text-sm text-gray-800 dark:text-gray-200">
        {cancelUnavailableReason && (
          <div className="rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
            {cancelUnavailableReason}
          </div>
        )}
        {occurrence.status === 'postponed' && occurrence.replacementSession && (
          <div className="rounded-lg border border-sky-200 bg-sky-50/90 px-3 py-2 text-xs text-sky-950 dark:border-sky-800 dark:bg-sky-950/40 dark:text-sky-100">
            This date was postponed. The replacement session is on{' '}
            <strong>
              {format(
                parse(occurrence.replacementSession.sessionDate, 'yyyy-MM-dd', new Date()),
                'EEEE, MMM d, yyyy'
              )}
            </strong>
            . To cancel, use <strong>Cancel session</strong> on the replacement date.
          </div>
        )}
        {!hasValidSubscription && (
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:border-gray-600 dark:bg-gray-800/50 dark:text-gray-300">
            An active subscription is required to cancel sessions or change the roster.
          </div>
        )}
        {occurrence.materializedSessionOverrideReason && (
          <div className="rounded-md border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 text-xs text-gray-700 dark:text-gray-300">
            <span className="font-medium text-gray-900 dark:text-white">Session note: </span>
            {occurrence.materializedSessionOverrideReason}
          </div>
        )}

        {occurrence.replacementSession && (
          <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20 px-3 py-2 text-xs">
            <div className="font-medium text-amber-900 dark:text-amber-100 flex items-center gap-1.5 mb-1">
              <RefreshCw className="h-3.5 w-3.5 shrink-0" />
              Replacement session (lesson_sessions)
            </div>
            <p className="text-amber-950 dark:text-amber-50">
              {format(
                parse(occurrence.replacementSession.sessionDate.slice(0, 10), 'yyyy-MM-dd', new Date()),
                'EEEE, MMM d, yyyy'
              )}{' '}
              · {formatTimeHHMM(occurrence.replacementSession.startTime)} –{' '}
              {formatTimeHHMM(occurrence.replacementSession.endTime)}
            </p>
            {occurrence.replacementSession.overrideReason && (
              <p className="mt-1 text-amber-900/90 dark:text-amber-200/90">
                {occurrence.replacementSession.overrideReason}
              </p>
            )}
          </div>
        )}

        {occurrence.originalSession && (
          <div className="rounded-md border border-sky-200 dark:border-sky-800 bg-sky-50/80 dark:bg-sky-900/20 px-3 py-2 text-xs">
            <div className="font-medium text-sky-900 dark:text-sky-100 flex items-center gap-1.5 mb-1">
              <CalendarClock className="h-3.5 w-3.5 shrink-0" />
              Original session (postponed from)
            </div>
            <p className="text-sky-950 dark:text-sky-50">
              {format(
                parse(occurrence.originalSession.sessionDate.slice(0, 10), 'yyyy-MM-dd', new Date()),
                'EEEE, MMM d, yyyy'
              )}{' '}
              · {formatTimeHHMM(occurrence.originalSession.startTime)} –{' '}
              {formatTimeHHMM(occurrence.originalSession.endTime)}
            </p>
            {occurrence.originalSession.overrideReason && (
              <p className="mt-1 text-sky-900/90 dark:text-sky-200/90">
                {occurrence.originalSession.overrideReason}
              </p>
            )}
          </div>
        )}

        <div id={LESSON_SESSION_TOUR_STEP_IDS.COACHES}>
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="font-medium text-gray-900 dark:text-white flex min-w-0 items-center gap-2">
              <Users className="h-4 w-4 shrink-0" />
              <span className="truncate">Coaches</span>
              {displayHasCoachOverride && (
                <span className="shrink-0 text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 dark:bg-amber-900/50 dark:text-amber-100">
                  Custom
                </span>
              )}
            </h4>
            <div className="flex shrink-0 items-center gap-2">
              {resolvingMaterializedSession &&
                allowSessionCoachOverride &&
                sessionScheduled &&
                !effectiveSessionId && (
                  <span className="text-[10px] text-gray-500 dark:text-gray-400">Loading…</span>
                )}
              {canAddCoach && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 shrink-0 gap-1 px-2 text-xs"
                  onClick={openAddCoachModal}
                >
                  <UserPlus className="h-3.5 w-3.5 shrink-0" />
                  Add coach
                </Button>
              )}
            </div>
          </div>
          {displayHasCoachOverride && displayCoachOverrideCreatedAt && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Roster customized on{' '}
              {format(
                displayCoachOverrideCreatedAt instanceof Date
                  ? displayCoachOverrideCreatedAt
                  : new Date(displayCoachOverrideCreatedAt),
                'MMM d, yyyy h:mm a'
              )}
            </p>
          )}
          {showMissingMaterializedSessionHint && (
            <p className="text-xs text-amber-800 dark:text-amber-200/90 rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50/90 dark:bg-amber-950/40 px-2.5 py-2 mb-2">
              No lesson session record matched this date (needed to change coaches for this session). Open{' '}
              <span className="font-medium">Edit lesson</span> and save once to refresh materialized sessions, or
              confirm your calendar has finished loading this month.
            </p>
          )}
          {assignedCoaches.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-xs">No coaches assigned.</p>
          ) : (
            <>
              {showCoachListGrid && (
                <div
                  className={`hidden sm:grid pb-1.5 mb-2.5 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 ${coachListGridCols} items-end gap-x-3`}
                >
                  <span className="min-w-0">Coach</span>
                  {showCoachAttendanceCol && (
                    <span className="flex items-center justify-center gap-1 text-center">
                      <span>Attendance</span>
                      <Tooltip
                        content="Coaches check in from My Attendance. You can mark or override coach attendance here."
                        side="top"
                      >
                        <button
                          type="button"
                          className="inline-flex rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-gray-500 dark:hover:text-gray-300"
                          aria-label="How coach attendance is recorded"
                        >
                          <Info className="h-3.5 w-3.5" aria-hidden />
                        </button>
                      </Tooltip>
                    </span>
                  )}
                  {showCoachRemoveCol && <span className="sr-only">Remove</span>}
                </div>
              )}
              <ul className="space-y-0 sm:space-y-2">
              {(coachAttendanceRoster.length > 0 ? coachAttendanceRoster : assignedCoaches.map((c) => ({
                coachId: c.id,
                firstName: c.firstName,
                lastName: c.lastName,
                status: 'scheduled' as const,
              }))).map((entry) => {
                const coach =
                  assignedCoaches.find((c) => c.id === entry.coachId) ??
                  coaches.find((c) => c.id === entry.coachId);
                const coachName = `${entry.firstName || coach?.firstName} ${entry.lastName || coach?.lastName}`.trim();
                return (
                  <li
                    key={entry.coachId}
                    className={
                      showCoachListGrid
                        ? `border-b border-gray-100 dark:border-gray-700 last:border-0 py-3 space-y-2.5 sm:py-0 sm:space-y-0 sm:pb-2 sm:grid sm:items-center sm:gap-x-3 sm:gap-y-1 ${coachListRowGridCols}`
                        : 'flex flex-wrap items-center justify-between gap-2 py-2 pb-2 border-b border-gray-100 dark:border-gray-700 last:border-0'
                    }
                  >
                    <div className="flex min-w-0 items-start justify-between gap-2 sm:block">
                      <div className="flex min-w-0 items-center gap-2">
                        <UserAvatar
                          firstName={entry.firstName || coach?.firstName}
                          lastName={entry.lastName || coach?.lastName}
                          size="sm"
                        />
                        <span className="font-medium">{coachName}</span>
                        {coach?.email && (
                          <span className="text-gray-500 text-xs">({coach.email})</span>
                        )}
                      </div>
                      {showCoachRemoveCol && (
                        <div className="shrink-0 sm:hidden">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 shrink-0 p-0 text-red-700 dark:text-red-300"
                            title="Remove coach from this session"
                            aria-label={`Remove ${coachName} from this session`}
                            disabled={removingSessionCoachId === entry.coachId}
                            onClick={() =>
                              setRemoveCoachConfirm({ coachId: entry.coachId, coachName })
                            }
                          >
                            {removingSessionCoachId === entry.coachId ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                              <UserMinus className="h-4 w-4" aria-hidden />
                            )}
                          </Button>
                        </div>
                      )}
                    </div>
                    {showCoachListGrid ? (
                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 sm:contents">
                        {showCoachAttendanceCol ? (
                          <div className="flex min-w-0 items-center gap-2 sm:flex sm:justify-center">
                            <span className="w-14 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden">
                              Attend.
                            </span>
                            {canManageRoster && coachAttendanceRoster.length > 0 ? (
                              <AttendanceToggle
                                current={entry.status}
                                loading={markingCoachId === entry.coachId}
                                disabled={
                                  markingCoachId != null && markingCoachId !== entry.coachId
                                }
                                onMark={(status) =>
                                  void handleMarkCoachAttendance(entry.coachId, status)
                                }
                              />
                            ) : loadingCoachAttendance && canManageRoster ? (
                              <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden />
                            ) : null}
                          </div>
                        ) : null}
                        {showCoachRemoveCol ? (
                          <div className="hidden sm:flex justify-end">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 shrink-0 p-0 text-red-700 dark:text-red-300"
                              title="Remove coach from this session"
                              aria-label={`Remove ${coachName} from this session`}
                              disabled={removingSessionCoachId === entry.coachId}
                              onClick={() =>
                                setRemoveCoachConfirm({ coachId: entry.coachId, coachName })
                              }
                            >
                              {removingSessionCoachId === entry.coachId ? (
                                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                              ) : (
                                <UserMinus className="h-4 w-4" aria-hidden />
                              )}
                            </Button>
                          </div>
                        ) : null}
                      </div>
                    ) : loadingCoachAttendance ? (
                      <Loader2 className="h-4 w-4 animate-spin text-gray-400" aria-hidden />
                    ) : null}
                  </li>
                );
              })}
              </ul>
            </>
          )}
        </div>

        <div id={LESSON_SESSION_TOUR_STEP_IDS.STUDENTS} className="flex-1 min-h-0 flex flex-col">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h4 className="font-medium text-gray-900 dark:text-white flex min-w-0 items-center gap-2">
              <User className="h-4 w-4 shrink-0" />
              <span className="truncate">
                Students this session
                {!loading && enrollments.length > 0 && (
                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                    {' '}
                    ({enrollments.length})
                  </span>
                )}
              </span>
            </h4>
            {canAddStudent && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 shrink-0 gap-1 px-2 text-xs"
                onClick={openEnrollModal}
              >
                <UserPlus className="h-3.5 w-3.5 shrink-0" />
                Add student
              </Button>
            )}
          </div>
          {lesson.lessonType === 'group' && !lesson.termId && sessionScheduled && hasValidSubscription && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
              Add or remove students from the roster is available for term-based group lessons.
            </p>
          )}
          {occurrence.status === 'cancelled' &&
            hasValidSubscription &&
            canRemoveSession && (
              <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/90 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-100">
                <p className="font-medium">This session is cancelled with no confirmed enrollments</p>
                <p className="mt-1">
                  Use <strong>Remove session</strong> in the footer to delete this date from the schedule
                  instead of leaving it marked cancelled.
                </p>
              </div>
            )}
          {loading ? (
            <p className="text-gray-500 text-xs">Loading…</p>
          ) : enrollments.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-xs">No enrollments for this session.</p>
          ) : (
            <>
              <div
                className={`hidden sm:grid pb-1.5 mb-2.5 border-b border-gray-200 dark:border-gray-700 text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 ${
                  canManageRoster
                    ? 'grid-cols-[minmax(0,1fr)_7rem_6.5rem_2rem] items-end gap-x-3'
                    : ''
                }`}
                aria-hidden
              >
                <span className="min-w-0">Student</span>
                {canManageRoster && (
                  <>
                    <span className="text-center whitespace-nowrap">Booking status</span>
                    <span className="text-center">Attendance</span>
                    <span className="sr-only">Remove</span>
                  </>
                )}
              </div>
              <ul className="space-y-0 sm:space-y-2 flex-1 min-h-0 overflow-y-auto pr-1 [scrollbar-width:thin]">
              {enrollments.map((row) => {
                const isMakeup =
                  row.enrollmentSource === 'makeup_session' || row.isMakeupBooking;
                const scopeLabel =
                  !isMakeup && row.bookingScope === 'session'
                    ? getOccurrenceScopeLabel({
                        bookingScope: row.bookingScope,
                        lessonType: lesson.lessonType,
                      })
                    : null;
                const fullScopeLabel =
                  lesson.lessonType === 'camp'
                    ? getCampEnrollmentListScopeLabel({
                        bookingScope: row.bookingScope,
                        sessionDateYmd: row.sessionDateYmd,
                        status: row.status,
                        enrollmentSource: row.enrollmentSource,
                      })
                    : getEnrollmentListScopeLabel({
                        bookingScope: row.bookingScope,
                        lessonType: lesson.lessonType,
                        lessonTermId: lesson.termId,
                      });
                return (
                <li
                  key={row.bookingId ?? row.makeupSessionReservationId ?? row.studentId}
                  className={`border-b border-gray-100 dark:border-gray-700 last:border-0 ${
                    canManageRoster
                      ? 'py-3 space-y-2.5 sm:py-0 sm:space-y-0 sm:pb-2 sm:grid sm:grid-cols-[minmax(0,1fr)_7rem_6.5rem_2rem] sm:items-center sm:gap-x-3 sm:gap-y-1'
                      : 'flex flex-wrap items-center gap-2 py-2 pb-2'
                  }`}
                >
                  <div className="flex min-w-0 items-start justify-between gap-2 sm:block">
                    <div className="min-w-0 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="font-medium">{row.studentName}</span>
                      {row.isMakeupBooking && (
                        <span className="text-[10px] px-1.5 py-0 rounded bg-purple-100 dark:bg-purple-900/50 text-purple-900 dark:text-purple-100">
                          Make-up
                        </span>
                      )}
                      {scopeLabel && <EnrollmentScopeChip label={scopeLabel} />}
                      {row.isReportedAbsent && (
                        <span className="text-[10px] px-1.5 py-0 rounded bg-slate-200 dark:bg-slate-700 text-slate-900 dark:text-slate-100">
                          Reported absent
                        </span>
                      )}
                      {fullScopeLabel && !scopeLabel ? (
                        <EnrollmentScopeChip label={fullScopeLabel} />
                      ) : null}
                      {shouldShowEnrollmentGuardian({
                        isSelf: row.isSelf,
                        isAdultStudent: row.isAdultStudent,
                        isManual: row.isManual,
                        hasLinkedGuardian: !!row.parentId,
                      }) &&
                        (row.parentName || row.parentEmail) && (
                        <span className="text-xs text-gray-500 w-full">
                          Guardian: {row.parentName || '—'}
                          {row.parentEmail ? ` · ${row.parentEmail}` : ''}
                        </span>
                      )}
                    </div>
                    {canManageRoster && row.bookingId ? (
                      <div className="shrink-0 sm:hidden">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 shrink-0 p-0 text-red-700 dark:text-red-300"
                          title="Remove from session"
                          aria-label={`Remove ${row.studentName} from session`}
                          disabled={
                            removingBookingId === row.bookingId ||
                            confirmingBookingId === row.bookingId
                          }
                          onClick={() =>
                            setRemoveConfirm({
                              bookingId: row.bookingId!,
                              studentName: row.studentName,
                              isTermEnrollment:
                                row.bookingScope === 'term' || row.bookingScope === 'package',
                            })
                          }
                        >
                          {removingBookingId === row.bookingId ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <UserMinus className="h-4 w-4" aria-hidden />
                          )}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  {canManageRoster && row.bookingId ? (
                    <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2 sm:contents">
                      <div className="flex min-w-0 items-center gap-2 sm:flex sm:justify-center">
                        <span className="w-14 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden">
                          Status
                        </span>
                        {row.status === 'pending' || row.status === 'confirmed' ? (
                          <EnrollmentBookingStatusChip
                            bookingId={row.bookingId}
                            status={row.status as EnrollmentBookingStatus}
                            updatingBookingId={confirmingBookingId}
                            disabled={removingBookingId === row.bookingId}
                            onStatusChange={handleBookingStatusChange}
                          />
                        ) : (
                          <span className="inline-flex h-6 w-[5.75rem] items-center justify-center text-[10px] text-gray-400 dark:text-gray-500 sm:w-[5.75rem] sm:mx-auto">
                            —
                          </span>
                        )}
                      </div>
                      <div className="flex min-w-0 items-center gap-2 sm:flex sm:justify-center">
                        <span className="w-14 shrink-0 text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 sm:hidden">
                          Attend.
                        </span>
                        <AttendanceToggle
                          current={row.attendanceStatus ?? 'scheduled'}
                          loading={markingBookingId === row.bookingId}
                          disabled={
                            removingBookingId === row.bookingId ||
                            confirmingBookingId === row.bookingId ||
                            (markingBookingId != null && markingBookingId !== row.bookingId)
                          }
                          onMark={(status) => void handleMarkAttendance(row.bookingId!, status)}
                        />
                      </div>
                      <div className="hidden sm:flex justify-end">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 shrink-0 p-0 text-red-700 dark:text-red-300"
                          title="Remove from session"
                          aria-label={`Remove ${row.studentName} from session`}
                          disabled={
                            removingBookingId === row.bookingId ||
                            confirmingBookingId === row.bookingId
                          }
                          onClick={() =>
                            setRemoveConfirm({
                              bookingId: row.bookingId!,
                              studentName: row.studentName,
                              isTermEnrollment:
                                row.bookingScope === 'term' || row.bookingScope === 'package',
                            })
                          }
                        >
                          {removingBookingId === row.bookingId ? (
                            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            <UserMinus className="h-4 w-4" aria-hidden />
                          )}
                        </Button>
                      </div>
                    </div>
                  ) : canManageRoster ? (
                    <>
                      <span className="hidden sm:block" aria-hidden />
                      <span className="hidden sm:block" aria-hidden />
                      <span className="hidden sm:block" aria-hidden />
                    </>
                  ) : null}
                </li>
                );
              })}
              </ul>
            </>
          )}
        </div>

      </div>
    </Drawer>
    <CancelSessionModal
      isOpen={cancelSessionModalOpen && isOpen}
      lesson={lesson}
      academyId={academyId}
      sessionDateYmd={occurrence.sessionDateYmd}
      sessionDateLabel={dateLabel}
      stackLevel="elevated"
      makeupLessonEnabled={makeupLessonEnabled}
      onCancel={() => setCancelSessionModalOpen(false)}
      onConfirm={handleCancelSessionConfirm}
    />
    <RemoveSessionModal
      isOpen={removeSessionModalOpen && isOpen}
      lessonName={occurrence.lessonName}
      sessionDateLabel={dateLabel}
      stackLevel="elevated"
      onCancel={() => setRemoveSessionModalOpen(false)}
      onConfirm={handleRemoveSessionConfirm}
    />
    <CancelTermLessonModal
      isOpen={cancelTermModalOpen && isOpen}
      lesson={lesson}
      academyId={academyId}
      stackLevel="elevated"
      variant={isStandalonePrivate ? 'package' : 'term'}
      makeupLessonEnabled={makeupLessonEnabled}
      onCancel={() => setCancelTermModalOpen(false)}
      onConfirm={handleCancelTermConfirm}
    />
    <RemoveStudentEnrollmentDialog
      key={
        removeConfirm
          ? `remove-${removeConfirm.bookingId}-${occurrence.sessionDateYmd}`
          : 'remove-closed'
      }
      isOpen={!!removeConfirm}
      studentName={removeConfirm?.studentName ?? ''}
      lessonName={occurrence.lessonName}
      sessionDateLabel={dateLabel}
      sessionDateYmd={occurrence.sessionDateYmd}
      bookingId={removeConfirm?.bookingId ?? ''}
      makeupLessonEnabled={makeupLessonEnabled}
      isTermEnrollment={removeConfirm?.isTermEnrollment ?? false}
      isSubmitting={!!removeConfirm && removingBookingId === removeConfirm.bookingId}
      onClose={() => setRemoveConfirm(null)}
      onConfirm={async (params) => {
        if (!removeConfirm) return;
        const ok = await handleRemoveFromLesson(removeConfirm.bookingId, params);
        if (ok) setRemoveConfirm(null);
      }}
    />
    <LessonEditRunningScheduleWarningDialog
      open={editRunningScheduleWarningOpen}
      onOpenChange={setEditRunningScheduleWarningOpen}
      variant={lesson.lessonType === 'camp' ? 'camp' : 'term_group'}
      termName={termForLesson?.name}
      onContinue={() => {
        onEdit();
        onClose();
      }}
    />
    <Dialog
      open={!!removeCoachConfirm}
      onOpenChange={(open) => {
        if (!open) {
          setRemoveCoachConfirm(null);
          setRemoveCoachNotifyEmail(true);
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Remove coach from this session?</DialogTitle>
          <DialogDescription>
            {removeCoachConfirm && (
              <>
                Remove <strong>{removeCoachConfirm.coachName}</strong> from this session only.
                Other dates keep the lesson&apos;s default coaches unless they have their own custom
                roster.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-start gap-3 py-1">
          <Checkbox
            id="remove-coach-notify-email"
            checked={removeCoachNotifyEmail}
            onCheckedChange={(checked) =>
              setRemoveCoachNotifyEmail(typeof checked === 'boolean' ? checked : false)
            }
            disabled={removingSessionCoachId != null}
          />
          <label
            htmlFor="remove-coach-notify-email"
            className="cursor-pointer text-sm text-gray-700 dark:text-gray-300"
          >
            Notify coach by email
          </label>
        </div>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => setRemoveCoachConfirm(null)}
            disabled={removingSessionCoachId != null}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleConfirmRemoveCoachFromSession()}
            disabled={removingSessionCoachId != null}
          >
            {removingSessionCoachId ? 'Removing…' : 'Remove coach'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {showAddCoachModal && (
      <AddCoachToSessionModal
        key={`add-coach-${lesson.id}-${occurrence.sessionDateYmd}-${addCoachModalInstance}`}
        isOpen={showAddCoachModal}
        onClose={() => setShowAddCoachModal(false)}
        academyId={academyId}
        lesson={lesson}
        sessionDateYmd={occurrence.sessionDateYmd}
        effectiveSessionId={effectiveSessionId}
        resolvedCoachIds={resolvedCoachIds}
        stackLevel="elevated"
        onSuccess={async () => {
          await refreshCoachRoster();
          await loadCoachAttendance();
          await onAfterAction();
        }}
      />
    )}
    {canShareLesson && (
      <LessonShareModal
        isOpen={shareModalOpen && isOpen}
        onClose={() => setShareModalOpen(false)}
        academyId={academyId}
        lessonId={lesson.id}
        lessonName={occurrence.lessonName}
        lessonType={lesson.lessonType}
        lessonTermId={lesson.termId}
        lessonIsActive={lesson.isActive}
        term={lesson.termId ? terms?.find((t) => t.id === lesson.termId) ?? null : null}
        enrollments={enrollments}
        onAfterNotify={loadEnrollments}
      />
    )}
    {showEnrollModal &&
      ((lesson.lessonType === 'group' && !!lesson.termId) ||
        lesson.lessonType === 'camp' ||
        lesson.lessonType === 'private') && (
      <EnrollStudentModal
        key={`owner-enroll-${lesson.id}-${occurrence.sessionDateYmd}-${enrollModalInstance}`}
        isOpen={showEnrollModal}
        onClose={() => setShowEnrollModal(false)}
        lessonId={lesson.id}
        lessonName={occurrence.lessonName}
        lessonTrackLabel={lessonTrackLabel}
        termId={
          lesson.lessonType === 'camp' || (lesson.lessonType === 'private' && !lesson.termId)
            ? undefined
            : lesson.termId
        }
        academyId={academyId}
        calendarEnrollmentContext={
          lesson.lessonType === 'camp'
            ? {
                kind: 'camp',
                sessionDateYmd: occurrence.sessionDateYmd,
                sessionId: effectiveSessionId,
              }
            : (lesson.lessonType === 'group' || lesson.lessonType === 'private') && lesson.termId
              ? {
                  kind: 'term_group',
                  sessionDateYmd: occurrence.sessionDateYmd,
                  sessionId: effectiveSessionId,
                }
              : null
        }
        onEnrollSuccess={async () => {
          await loadEnrollments();
          await onAfterAction();
        }}
        canEnrollSingleSession={hasPerSessionStaffEnrollmentPrice(lesson)}
        maxCapacity={lesson.maxCapacity}
        enrolledCount={sessionEnrollmentCount}
        showPastEnrollmentWarning={sessionIsPast}
      />
    )}
    </>
  );
}
